import React, { useState } from 'react';
import { Search, Calendar, X, ChevronDown, SlidersHorizontal } from 'lucide-react';

interface FiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  dateFrom?: string;
  onDateFromChange?: (value: string) => void;
  dateTo?: string;
  onDateToChange?: (value: string) => void;
  searchPlaceholder?: string;
  onClearFilters?: () => void;
  // Platform filter props
  selectedPlatform?: string;
  onPlatformChange?: (value: string) => void;
  platformOptions?: string[];
  platformLabel?: string;
  // Client filter props
  selectedClienteId?: string;
  onClienteChange?: (value: string) => void;
  clienteOptions?: { id: string; name: string }[];
  // Status filter props
  selectedStato?: string;
  onStatoChange?: (value: string) => void;
  statoOptions?: { value: string; label: string }[];
  // Results count
  resultsCount?: number;
}

const Filters: React.FC<FiltersProps> = ({
  searchTerm,
  onSearchChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  searchPlaceholder = "Cerca...",
  onClearFilters,
  selectedPlatform,
  onPlatformChange,
  platformOptions,
  selectedClienteId,
  onClienteChange,
  clienteOptions,
  selectedStato,
  onStatoChange,
  statoOptions,
  resultsCount
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const showDateFilters = dateFrom !== undefined && dateTo !== undefined;
  const hasActiveFilters = searchTerm || dateFrom || dateTo || selectedPlatform || selectedClienteId || selectedStato;

  // Clienti sempre in ordine alfabetico (italiano, case-insensitive)
  const sortedClienteOptions = clienteOptions
    ? [...clienteOptions].sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
    : clienteOptions;

  // Conta i filtri attivi per il badge mobile
  const activeFiltersCount = [
    selectedPlatform,
    selectedClienteId,
    selectedStato,
    dateFrom,
    dateTo
  ].filter(Boolean).length;

  return (
    <div className="space-y-3 mb-4 overflow-x-hidden">
      {/* Riga principale: Search + Toggle filtri su mobile */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search - full width su mobile */}
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 sm:pl-9 pr-3 sm:pr-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all duration-200 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
          />
          <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
        </div>

        {/* Toggle filtri avanzati - solo mobile */}
        <button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="sm:hidden flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 active:bg-slate-100 transition-all duration-200 touch-manipulation relative"
        >
          <SlidersHorizontal className="w-4 h-4" />
          <ChevronDown className={`w-3 h-3 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#fdf751] text-slate-900 text-[10px] font-bold rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* Filtri inline su desktop */}
        <div className="hidden sm:flex items-center gap-3 flex-wrap">
          {/* Client filter */}
          {clienteOptions && onClienteChange && (
            <select
              value={selectedClienteId || ''}
              onChange={(e) => onClienteChange(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 min-w-[140px] lg:min-w-[150px]"
            >
              <option value="">Tutti i clienti</option>
              {sortedClienteOptions!.map(cliente => (
                <option key={cliente.id} value={cliente.id}>{cliente.name}</option>
              ))}
            </select>
          )}

          {/* Platform filter */}
          {platformOptions && onPlatformChange && (
            <select
              value={selectedPlatform || ''}
              onChange={(e) => onPlatformChange(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 min-w-[120px] lg:min-w-[130px]"
            >
              <option value="">Piattaforma</option>
              {platformOptions.map(platform => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
          )}

          {/* Status filter */}
          {statoOptions && onStatoChange && (
            <select
              value={selectedStato || ''}
              onChange={(e) => onStatoChange(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 min-w-[110px] lg:min-w-[120px]"
            >
              <option value="">Stato</option>
              {statoOptions.map(stato => (
                <option key={stato.value} value={stato.value}>{stato.label}</option>
              ))}
            </select>
          )}

          {/* Date From */}
          {showDateFilters && onDateFromChange && (
            <div className="relative">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="w-[130px] lg:w-[140px] pl-8 pr-2 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 transition-all duration-200 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
              />
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            </div>
          )}

          {/* Date To */}
          {showDateFilters && onDateToChange && (
            <div className="relative">
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="w-[130px] lg:w-[140px] pl-8 pr-2 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 transition-all duration-200 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
              />
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            </div>
          )}

          {/* Clear button */}
          {onClearFilters && hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="p-2 rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700 transition-all duration-200"
              title="Pulisci filtri"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Results count - desktop */}
        {resultsCount !== undefined && (
          <span className="hidden md:inline text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {resultsCount} risultati
          </span>
        )}
      </div>

      {/* Filtri espandibili su mobile */}
      {showMobileFilters && (
        <div className="sm:hidden bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl p-3 space-y-3 animate-fade-in">
          {/* Client filter */}
          {clienteOptions && onClienteChange && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cliente</label>
              <select
                value={selectedClienteId || ''}
                onChange={(e) => onClienteChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
              >
                <option value="">Tutti i clienti</option>
                {sortedClienteOptions!.map(cliente => (
                  <option key={cliente.id} value={cliente.id}>{cliente.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Platform filter */}
          {platformOptions && onPlatformChange && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Piattaforma</label>
              <select
                value={selectedPlatform || ''}
                onChange={(e) => onPlatformChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
              >
                <option value="">Tutte le piattaforme</option>
                {platformOptions.map(platform => (
                  <option key={platform} value={platform}>{platform}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status filter */}
          {statoOptions && onStatoChange && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Stato</label>
              <select
                value={selectedStato || ''}
                onChange={(e) => onStatoChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
              >
                <option value="">Tutti gli stati</option>
                {statoOptions.map(stato => (
                  <option key={stato.value} value={stato.value}>{stato.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Date filters */}
          {showDateFilters && (
            <div className="grid grid-cols-2 gap-3">
              {onDateFromChange && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Da</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => onDateFromChange(e.target.value)}
                      className="w-full pl-8 pr-2 py-2.5 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 transition-all duration-200 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
                    />
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  </div>
                </div>
              )}
              {onDateToChange && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">A</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => onDateToChange(e.target.value)}
                      className="w-full pl-8 pr-2 py-2.5 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 transition-all duration-200 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 touch-manipulation"
                    />
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clear filters button mobile */}
          {onClearFilters && hasActiveFilters && (
            <button
              onClick={() => {
                onClearFilters();
                setShowMobileFilters(false);
              }}
              className="w-full py-2.5 px-4 bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-gray-600 active:bg-slate-300 transition-all duration-200 touch-manipulation flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Pulisci filtri
            </button>
          )}

          {/* Results count - mobile */}
          {resultsCount !== undefined && (
            <div className="text-center pt-2 border-t border-slate-200 dark:border-gray-700">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {resultsCount} risultati trovati
              </span>
            </div>
          )}
        </div>
      )}

      {/* Results count mobile (quando filtri chiusi) */}
      {!showMobileFilters && resultsCount !== undefined && (
        <div className="sm:hidden text-xs text-slate-500 dark:text-slate-400">
          {resultsCount} risultati
        </div>
      )}
    </div>
  );
};

export default Filters;
